"use client";

/**
 * MessageAttachments — renders the file attachments on a rendered message
 * bubble (both user and assistant messages).
 *
 * For user messages this shows the file parts the user attached before
 * sending (thumbnails for images, chips with icons for other files).  The
 * attachment ``url`` is a blob:/data: URL for the optimistic message.  For
 * historical messages, the file parts are parsed from the
 * ``<uploaded_files>`` block in the message text by
 * ``extractUploadedFiles()`` — the server linkifies the paths to signed
 * ``/files/d/{jwt}`` download URLs at read time.
 *
 * For assistant messages the file-links linkifier (server-side, read-time)
 * already replaces workspace paths in the text with signed ``/files/d/{jwt}``
 * download links, so those are rendered by ``ParsedMessageContent`` as
 * normal markdown links.  This component therefore renders nothing for
 * assistant messages unless they carry explicit file parts.
 *
 * **File viewer integration:** When ``onFileClick`` is provided and a file
 * is Markdown, HTML, PDF, DOCX, image, or a CodeMirror-supported source file (detected
 * via signed JWT metadata), clicking the pill calls ``onFileClick`` instead
 * of triggering a download. The parent (``ChatPage``) opens the corresponding
 * viewer. Other files remain download links.
 */

import { cn } from "@/lib/utils";
import { iconForFile } from "@/components/ui/file-pill";
import type { RenderFilePart } from "@/lib/render-items";
import {
  isDocxFile,
  isHtmlFile,
  isXlsxFile,
  isImageFile,
  isMarkdownFile,
  isPdfFile,
  isSourceFile,
  isViewableFile,
  openFileUrl,
} from "@/lib/file-links";
import { useCopyToClipboard } from "@/hooks/use-copy-to-clipboard";
import { useTranslation } from "react-i18next";
import {
  Copy,
  Check,
  Eye,
  Download,
} from "lucide-react";

// ─── Copy-link button (shared by pill + default variants) ──────────────────

/**
 * Tiny icon button that copies a file's download URL to the clipboard.
 *
 * Shows a ``Copy`` icon by default and flips to a ``Check`` icon for ~2 s
 * after a successful copy.  ``stopPropagation`` on click prevents the
 * event from reaching the parent ``<a>`` / ``<button>`` so the file is
 * not downloaded or opened in the viewer when the user only meant to
 * copy the link.
 *
 * Renders ``null`` when ``url`` is falsy.
 */
function CopyLinkButton({
  url,
  className,
}: {
  url?: string;
  className?: string;
}) {
  const { t } = useTranslation();
  const { copied, copy } = useCopyToClipboard(2000);
  if (!url) return null;
  const Icon = copied ? Check : Copy;
  return (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        void copy(url);
      }}
      className={cn(
        "shrink-0 rounded p-0.5 text-muted-foreground/60 transition-colors hover:text-foreground",
        "focus:outline-none focus-visible:ring-1 focus-visible:ring-ring",
        className,
      )}
      aria-label={copied ? t("attachment.linkCopied") : t("attachment.copyLink")}
      title={copied ? t("attachment.copied") : t("attachment.copyLink")}
      data-testid="file-copy-link-button"
      data-copied={copied || undefined}
    >
      <Icon className="size-3" />
    </button>
  );
}

// ─── File-extension badge (shared by pill + default variants) ─────────────

function FileExtensionBadge({ label }: { label: string }) {
  const extension = label.includes(".")
    ? (label.split(".").pop() ?? "").toLowerCase()
    : "";

  if (!extension) return null;

  return (
    <span
      className="shrink-0 rounded bg-muted px-1 py-0.5 font-mono text-[10px] font-medium uppercase tracking-wide text-muted-foreground"
      data-testid="message-file-extension-badge"
    >
      {extension}
    </span>
  );
}

// ─── Component ───────────────────────────────────────────────────────────────

export type MessageAttachmentsProps = {
  files: RenderFilePart[];
  className?: string;
  /**
   * Layout variant:
   * - ``"default"`` (default): image thumbnails in a grid, non-image files
   *   as a vertical list of chips. Used for user message attachments.
   * - ``"pill"``: all files (including images) as a horizontal row of compact
   *   pills. Used for assistant message attachment pills, where the file is
   *   also linked inline in the message text (dual representation).
   */
  variant?: "default" | "pill";
  /**
   * Called when the user clicks a Markdown, HTML, PDF, image, or supported source
   * file pill. The parent should open the corresponding viewer with this URL
   * + filename. Other files remain download links and do not invoke this
   * callback.
   *
   * When omitted, all files (including viewable files) are download links.
   */
  onFileClick?: (url: string, fileName: string) => void;
};

/**
 * Render file attachments inside a message bubble.
 *
 * ``variant="default"`` (default): image attachments with a URL are shown as
 * a grid of thumbnails (clickable to open the full image).  Non-image
 * attachments are shown as a vertical list of download-link chips.
 *
 * ``variant="pill"``: all files are rendered as a horizontal wrapping row of
 * compact download pills.  Intended for assistant messages where the file is
 * also linked inline in the text — the pill is a secondary visual affordance,
 * not the primary representation.
 *
 * Renders ``null`` when ``files`` is empty.
 */
export function MessageAttachments({
  files,
  className,
  variant = "default",
  onFileClick,
}: MessageAttachmentsProps) {
  const { t } = useTranslation();
  if (!files || files.length === 0) return null;

  const images = files.filter(
    (f) => (f.mediaType ?? "").startsWith("image/") && f.url,
  );
  const others = files.filter(
    (f) => !(f.mediaType ?? "").startsWith("image/") || !f.url,
  );

  // ── Pill variant: horizontal row of compact pills for all files ─────────
  if (variant === "pill") {
    return (
      <div
        className={cn("flex flex-wrap gap-1.5", className)}
        data-testid="message-attachments-pill"
      >
        {files.map((f, i) => {
          const Icon = iconForFile(f.mediaType, f.filename);
          const label = f.filename ?? t("attachment.file");
          const isImage = (f.mediaType ?? "").startsWith("image/");
          // When onFileClick is provided and the file is viewable, render a
          // <button> that opens its viewer instead of an <a> download.
          const isMd = f.url ? isMarkdownFile(f.url, f.filename) : false;
          const isHtml = f.url ? isHtmlFile(f.url, f.filename) || isXlsxFile(f.url, f.filename) : false;
          const isPdf = f.url ? isPdfFile(f.url, f.filename) : false;
          const isDocx = f.url ? isDocxFile(f.url, f.filename) : false;
          const isImageViewer = f.url ? isImageFile(f.url, f.filename) : false;
          const isSource = f.url ? isSourceFile(f.url, f.filename) : false;
          const openInViewer = onFileClick
            && !!f.url && isViewableFile(f.url, label)
            && !f.unshared;
          const hasUrl = !!f.url;

          // Visual content shared by all pill types (icon + label + action).
          const pillContent = (
            <>
              {isImage && f.url ? (
                <img
                  src={f.url}
                  alt={label}
                  className="size-5 shrink-0 rounded object-cover"
                  loading="lazy"
                />
              ) : (
                <Icon className="size-4 shrink-0 text-muted-foreground" />
              )}
              <span className="truncate text-xs font-medium" title={label}>
                {label}
              </span>
              <FileExtensionBadge label={label} />
              {openInViewer ? (
                <Eye className="size-3 shrink-0 text-muted-foreground/60 ml-auto" />
              ) : hasUrl && !f.unshared ? (
                <Download className="size-3 shrink-0 text-muted-foreground/60 ml-auto" />
              ) : null}
            </>
          );

          // Unshared pills: dashed border, no copy button, no interactive
          // element.  Plain <div> — a file:// URL can't be fetched.
          if (f.unshared) {
            return (
              <div
                key={`${label}-${i}`}
                className={cn(
                  "flex items-center gap-1.5 rounded-md border border-dashed border-border bg-muted/30 px-2 py-1",
                  "max-w-[200px]",
                )}
                data-testid="message-file-attachment-pill"
                data-unshared="true"
              >
                {pillContent}
                <span
                  className="ml-auto rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground"
                  data-testid="message-file-attachment-unshared-badge"
                >
                  {t("attachment.unshared")}
                </span>
              </div>
            );
          }

          // Shared pills: wrapper <div> with visual styling, interactive
          // element + copy button as siblings (HTML spec forbids nesting
          // <button> inside <a> or <button>).
          return (
            <div
              key={`${label}-${i}`}
              className={cn(
                "flex items-center gap-0.5 rounded-md border border-border bg-muted/40",
                "max-w-[200px] transition-colors hover:bg-muted",
              )}
            >
              {openInViewer ? (
                <button
                  type="button"
                  onClick={() => openFileUrl(f.url!, label, onFileClick)}
                  className={cn(
                    "flex flex-1 items-center gap-1.5 px-2 py-1 min-w-0 text-left cursor-pointer",
                  )}
                  data-testid="message-file-attachment-pill"
                  data-md-viewer={isMd ? "true" : undefined}
                  data-html-viewer={isHtml ? "true" : undefined}
                  data-pdf-viewer={isPdf ? "true" : undefined}
                  data-docx-viewer={isDocx ? "true" : undefined}
                  data-image-viewer={isImageViewer ? "true" : undefined}
                  data-source-viewer={isSource ? "true" : undefined}
                >
                  {pillContent}
                </button>
              ) : hasUrl ? (
                <a
                  href={f.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  download={f.filename}
                  className={cn(
                    "flex flex-1 items-center gap-1.5 px-2 py-1 min-w-0",
                  )}
                  data-testid="message-file-attachment-pill"
                >
                  {pillContent}
                </a>
              ) : (
                <div
                  className={cn(
                    "flex flex-1 items-center gap-1.5 px-2 py-1 min-w-0",
                  )}
                  data-testid="message-file-attachment-pill"
                >
                  {pillContent}
                </div>
              )}
              {hasUrl && (
                <CopyLinkButton url={f.url} className="py-1 pr-1.5" />
              )}
            </div>
          );
        })}
      </div>
    );
  }

  // ── Default variant: image grid + vertical chip list ───────────────────
  return (
    <div className={cn("flex flex-col gap-2", className)} data-testid="message-attachments">
      {/* Image thumbnails grid */}
      {images.length > 0 && (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {images.map((f, i) => {
            const label = f.filename ?? t("attachment.uploadedImage");
            const openInViewer = Boolean(
              onFileClick
              && f.url
              && isImageFile(f.url, f.filename)
              && !f.unshared,
            );
            const imageContent = (
              <>
                <img
                  src={f.url}
                  alt={label}
                  className="h-32 w-full object-cover"
                  loading="lazy"
                />
                {openInViewer && (
                  <span className="absolute right-2 top-2 rounded bg-background/80 p-1 text-foreground shadow-sm">
                    <Eye className="size-3.5" aria-hidden="true" />
                    <span className="sr-only">{t("attachment.openInViewer")}</span>
                  </span>
                )}
              </>
            );

            return openInViewer ? (
              <button
                key={`${label}-${i}`}
                type="button"
                onClick={() => onFileClick!(f.url!, label)}
                className="relative block overflow-hidden rounded-md border border-border bg-muted/30 text-left transition-opacity hover:opacity-90"
                title={f.filename}
                data-testid="message-image-attachment"
                data-image-viewer="true"
              >
                {imageContent}
              </button>
            ) : (
              <a
                key={`${label}-${i}`}
                href={f.url}
                target="_blank"
                rel="noopener noreferrer"
                className="relative block overflow-hidden rounded-md border border-border bg-muted/30 transition-opacity hover:opacity-90"
                title={f.filename}
                data-testid="message-image-attachment"
              >
                {imageContent}
              </a>
            );
          })}
        </div>
      )}

      {/* Non-image file chips */}
      {others.length > 0 && (
        <div className="flex flex-col gap-1.5">
          {others.map((f, i) => {
            const Icon = iconForFile(f.mediaType, f.filename);
            const label = f.filename ?? t("attachment.file");
            // When onFileClick is provided and the file is viewable, render a
            // <button> that opens its viewer instead of an <a> download.
            const isMd = f.url ? isMarkdownFile(f.url, f.filename) : false;
            const isHtml = f.url ? isHtmlFile(f.url, f.filename) || isXlsxFile(f.url, f.filename) : false;
            const isPdf = f.url ? isPdfFile(f.url, f.filename) : false;
            const isDocx = f.url ? isDocxFile(f.url, f.filename) : false;
            const isImageViewer = f.url ? isImageFile(f.url, f.filename) : false;
            const isSource = f.url ? isSourceFile(f.url, f.filename) : false;
            const openInViewer = onFileClick
              && !!f.url && isViewableFile(f.url, label)
              && !f.unshared;
            const hasUrl = !!f.url;

            // Visual content shared by all chip types (icon + label + action).
            const chipContent = (
              <>
                <Icon className="size-4 shrink-0 text-muted-foreground" />
                <span className="truncate text-xs font-medium" title={label}>
                  {label}
                </span>
                <FileExtensionBadge label={label} />
                {openInViewer ? (
                  <Eye className="size-3 shrink-0 text-muted-foreground/60 ml-auto" />
                ) : hasUrl && !f.unshared ? (
                  <Download className="size-3 shrink-0 text-muted-foreground/60 ml-auto" />
                ) : null}
              </>
            );

            // ``f.unshared`` means the server didn't rewrite the
            // ``[label](file://…)`` link (older transcript, rewriter
            // blow-up, etc.). We surface the file as a chip with an
            // "(unshared)" badge rather than handing the browser a
            // ``file://`` href that it can't resolve.
            if (f.unshared) {
              // Deliberately *not* an `<a>` — a `file://` URL is not
              // something the browser can fetch from chat-bridge's
              // reverse-proxy origin. The plain `div` is the right
              // element.
              return (
                <div
                  key={`${label}-${i}`}
                  className={cn(
                    "flex items-center gap-2 rounded-md border border-dashed border-border bg-muted/30 px-2.5 py-1.5",
                    "max-w-[260px]",
                  )}
                  data-testid="message-file-attachment"
                  data-unshared="true"
                  data-file-url={f.url}
                >
                  {chipContent}
                  <span
                    className="ml-auto rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground"
                    data-testid="message-file-attachment-unshared-badge"
                  >
                    {t("attachment.unshared")}
                  </span>
                </div>
              );
            }

            // Shared chips: wrapper <div> with visual styling, interactive
            // element + copy button as siblings (HTML spec forbids nesting
            // <button> inside <a> or <button>).
            return (
              <div
                key={`${label}-${i}`}
                className={cn(
                  "flex items-center gap-1 rounded-md border border-border bg-muted/40",
                  "max-w-[260px] transition-colors hover:bg-muted",
                )}
              >
                {openInViewer ? (
                  <button
                    type="button"
                    onClick={() => openFileUrl(f.url!, label, onFileClick)}
                    className={cn(
                      "flex flex-1 items-center gap-2 px-2.5 py-1.5 min-w-0 text-left cursor-pointer",
                    )}
                    data-testid="message-file-attachment"
                    data-md-viewer={isMd ? "true" : undefined}
                    data-html-viewer={isHtml ? "true" : undefined}
                    data-pdf-viewer={isPdf ? "true" : undefined}
                    data-docx-viewer={isDocx ? "true" : undefined}
                    data-image-viewer={isImageViewer ? "true" : undefined}
                    data-source-viewer={isSource ? "true" : undefined}
                  >
                    {chipContent}
                  </button>
                ) : hasUrl ? (
                  <a
                    href={f.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    download={f.filename}
                    className={cn(
                      "flex flex-1 items-center gap-2 px-2.5 py-1.5 min-w-0",
                    )}
                    data-testid="message-file-attachment"
                  >
                    {chipContent}
                  </a>
                ) : (
                  <div
                    className={cn(
                      "flex flex-1 items-center gap-2 px-2.5 py-1.5 min-w-0",
                    )}
                    data-testid="message-file-attachment"
                  >
                    {chipContent}
                  </div>
                )}
                {hasUrl && (
                  <CopyLinkButton url={f.url} className="py-1.5 pr-2" />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
